import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { reportScreen, restoreSession } from './api.ts'
import { waitForNativeConfiguration } from './bridge.ts'
import { applyTheme, productScreens, routeScreen, t, type ScreenId } from './contracts.ts'
import { navigate } from './router.ts'
import type { Account } from './types.ts'
import { ChannelDetailScreen } from './screens/ChannelDetail.tsx'
import { ChannelMembersScreen } from './screens/ChannelMembers.tsx'
import { ChannelJoinScreen, ChannelPreviewScreen, ChannelsScreen } from './screens/ChannelScreens.tsx'
import { ChannelInviteScreen, ComposeScreen, DeliveryDetailScreen, DeliveryLogScreen, EndpointCreateScreen, EndpointDetailScreen, EndpointsScreen, EndpointSettingsScreen } from './screens/EndpointScreens.tsx'
import { InboxScreen, NotificationDetailScreen } from './screens/InboxScreens.tsx'
import { AuthCallbackScreen, DocsScreen, LandingScreen, NotFoundScreen, SignInPasswordScreen, SignInScreen, VerifyCodeScreen } from './screens/PublicScreens.tsx'
import { DevicesScreen, SettingsScreen } from './screens/SettingsScreens.tsx'

const publicScreens = new Set<ScreenId>(['landing', 'signIn', 'verifyCode', 'signInPassword', 'authCallback', 'docs', 'notFound', 'channelPreview'])
/** Screens that own the whole viewport instead of sitting inside the dashboard shell. */
const chromeless: ScreenId[] = ['landing', 'signIn', 'verifyCode', 'signInPassword', 'authCallback', 'docs', 'notFound']
const navItems = productScreens.tabs.items

function Shell({ screen, children }: { screen: ScreenId; children: ReactNode }) {
  const active = screen === 'notificationDetail' ? 'inbox' : screen.startsWith('channel') ? 'channels' : screen.startsWith('endpoint') || screen.startsWith('delivery') || screen === 'compose' ? 'endpoints' : screen === 'devices' ? 'settings' : screen
  return <div className="app-shell"><aside className="sidebar"><div className="brand">{t('app.name')}</div><nav aria-label={t('app.name')}>{navItems.map((item) => <button className={`nav-item ${active === item.id ? 'active' : ''}`} aria-current={active === item.id ? 'page' : undefined} onClick={() => navigate(`/${item.id}`)} key={item.id}>{t(item.labelKey)}</button>)}</nav></aside>{children}</div>
}

export function App() {
  const [location, setLocation] = useState(() => window.location.pathname)
  const [account, setAccount] = useState<Account | null>(null)
  const [booting, setBooting] = useState(true)
  const route = useMemo(() => routeScreen(location), [location])

  useEffect(() => {
    const listener = () => setLocation(window.location.pathname)
    const expired = () => {
      sessionStorage.setItem('bellhook.pendingPath', window.location.pathname)
      sessionStorage.setItem('bellhook.sessionMessage', t('common.error.session'))
      setAccount(null)
      navigate('/signin', true)
    }
    const nativeSession = (event: Event) => {
      const authenticated = (event as CustomEvent<{ authenticated?: boolean }>).detail?.authenticated === true
      if (!authenticated) {
        setAccount(null)
        return
      }
      void restoreSession().then((session) => {
        setAccount(session?.account ?? null)
        if (session) applyTheme(session.account.appearance)
      })
    }
    window.addEventListener('popstate', listener)
    window.addEventListener('bellhook:session-expired', expired)
    window.addEventListener('bellhook:native-session-changed', nativeSession)
    void waitForNativeConfiguration().then(restoreSession).then((session) => {
      if (session) { setAccount(session.account); applyTheme(session.account.appearance) }
      setBooting(false)
    })
    return () => {
      window.removeEventListener('popstate', listener)
      window.removeEventListener('bellhook:session-expired', expired)
      window.removeEventListener('bellhook:native-session-changed', nativeSession)
    }
  }, [])

  useEffect(() => { void reportScreen(route.id) }, [route.id, location])
  useEffect(() => {
    if (booting) return
    if (location === '/' && account) navigate('/inbox', true)
    else if (!account && !publicScreens.has(route.id)) {
      sessionStorage.setItem('bellhook.pendingPath', location)
      navigate('/signin', true)
    }
  }, [account, booting, location, route.id])

  if (booting) return <main className="hero"><span className="spinner" aria-label={t('common.loading')} /></main>
  const p = route.params
  let content: ReactNode
  switch (route.id) {
    case 'landing': content = account ? null : <LandingScreen />; break
    case 'signIn': content = <SignInScreen />; break
    case 'verifyCode': content = <VerifyCodeScreen onSession={setAccount} />; break
    case 'signInPassword': content = <SignInPasswordScreen onSession={setAccount} />; break
    case 'authCallback': content = <AuthCallbackScreen onSession={setAccount} />; break
    case 'inbox': content = <InboxScreen />; break
    case 'notificationDetail': content = <NotificationDetailScreen id={p.id} />; break
    case 'channels': content = <ChannelsScreen />; break
    case 'channelJoin': content = <ChannelJoinScreen />; break
    case 'channelPreview': content = <ChannelPreviewScreen code={p.code} signedIn={Boolean(account)} />; break
    case 'channelDetail': content = <ChannelDetailScreen id={p.id} />; break
    case 'endpoints': content = <EndpointsScreen />; break
    case 'endpointCreate': content = account ? <EndpointCreateScreen account={account} /> : null; break
    case 'endpointDetail': content = <EndpointDetailScreen id={p.id} />; break
    case 'compose': content = <ComposeScreen id={p.id} />; break
    case 'channelMembers': content = <ChannelMembersScreen id={p.id} />; break
    case 'channelInvite': content = <ChannelInviteScreen id={p.id} />; break
    case 'endpointSettings': content = <EndpointSettingsScreen id={p.id} />; break
    case 'deliveryLog': content = <DeliveryLogScreen id={p.id} />; break
    case 'deliveryDetail': content = <DeliveryDetailScreen id={p.id} deliveryId={p.deliveryId} />; break
    case 'devices': content = <DevicesScreen />; break
    case 'settings': content = account ? <SettingsScreen account={account} onAccount={setAccount} onSignedOut={() => setAccount(null)} /> : null; break
    case 'docs': content = <DocsScreen />; break
    default: content = <NotFoundScreen signedIn={Boolean(account)} />
  }
  if (!content) return null
  return account && !chromeless.includes(route.id) ? <Shell screen={route.id}>{content}</Shell> : content
}

export const implementedWebScreens = [
  'landing', 'signIn', 'verifyCode', 'signInPassword', 'authCallback',
  'inbox', 'inboxFilter', 'notificationDetail', 'channels',
  'channelJoin', 'channelPreview', 'channelDetail', 'channelReport', 'endpoints', 'endpointCreate',
  'endpointDetail', 'compose', 'channelMembers', 'channelInvite', 'endpointSettings', 'deliveryLog',
  'deliveryDetail', 'devices', 'settings', 'docs', 'notFound',
] as const
